import { createContextKey, createContextValues } from '@connectrpc/connect'
import type { CallOptions } from '@connectrpc/connect'

/**
 * Context key to mark a call as sensitive (payload won't be logged to Sentry).
 * Default is `true` (sensitive) - payloads are NOT logged by default.
 *
 * To opt-out and allow payload logging, use `meta: { sensitive: false }` in your hook options:
 *
 * @example
 * ```ts
 * // Default: payload is NOT logged (sensitive)
 * const mutation = useUpscalerDeleteMutation()
 *
 * // Opt-out: payload WILL be logged to Sentry on error
 * const mutation = useUpscalerDeleteMutation({ meta: { sensitive: false } })
 * ```
 */
export const sensitiveCallKey = createContextKey<boolean>(true)

/**
 * Context key to explicitly disable automatic Sentry reporting in the RPC
 * interceptor.
 *
 * This is opt-in and should be paired with explicit `Sentry.capture*` handling
 * in mutation/query `onError` callbacks.
 */
export const sentryEnabledKey = createContextKey<boolean>(true)

export const skipDefaultRequestHeadersKey = createContextKey<boolean>(false)

/**
 * Context key to tag the caller/source of an RPC call for debugging.
 * Included in Sentry extra data when present.
 *
 * @example
 * ```ts
 * useGetSessionQuery(variables, { meta: { source: 'processing-queue-watcher' } })
 * ```
 */
export const sourceKey = createContextKey<string | undefined>(undefined)

/**
 * Creates CallOptions for non-sensitive calls (payload will be logged to Sentry).
 * Returns undefined if no supported meta flags are set.
 *
 * @param meta - Optional meta object from React Query options. Supported keys:
 *   - **`sensitive: false`** — allow request payload to be included in Sentry error reports for this call.
 *   - **`sentry: false`** — skip automatic `Sentry.captureException` in the RPC interceptor for this call. Use only when errors are handled explicitly (for example in `onError` with `Sentry.capture*` or app-specific helpers that report expected failures).
 *   - **`skipDefaultRequestHeaders: true`** — skip headers supplied by the app's default provider.
 *     Shared queries can then keep their request workspace aligned with their cache key.
 *     For example, a Personal balance query must not inherit Studio's selected team header.
 *     Authentication and other interceptors still run.
 *   - **`headers: Headers`** — attach headers to this request only.
 *   - **`source: string`** — tag the caller/source for debugging in Sentry.
 * @returns CallOptions with context values, or undefined for default behavior
 */
export function createCallOptions(
  meta?: Record<string, unknown> | undefined,
): CallOptions | undefined {
  const skipDefaultRequestHeaders = meta?.skipDefaultRequestHeaders === true
  const headers = meta?.headers instanceof Headers ? meta.headers : undefined
  const markAsNonSensitive = meta?.sensitive === false
  const disableSentryCapture = meta?.sentry === false
  const source = typeof meta?.source === 'string' ? meta.source : undefined

  if (
    !markAsNonSensitive &&
    !disableSentryCapture &&
    !source &&
    !headers &&
    !skipDefaultRequestHeaders
  ) {
    return undefined
  }

  const contextValues = createContextValues()
  if (skipDefaultRequestHeaders) {
    contextValues.set(skipDefaultRequestHeadersKey, true)
  }
  if (markAsNonSensitive) {
    contextValues.set(sensitiveCallKey, false)
  }
  if (disableSentryCapture) {
    contextValues.set(sentryEnabledKey, false)
  }
  if (source) {
    contextValues.set(sourceKey, source)
  }

  return { contextValues, headers }
}
